import { formatDelta, formatMoney } from "./evalOutput";

/**
 * One-line answer to "what did the Eval model actually change in Step 1?".
 *
 * Step 1 output is mostly prose, so a reviewer scanning the raw panes cannot
 * tell a rewritten sentence from a different qualifying income. This separates
 * the two: money and status are regressions, wording is not.
 *
 * @param {Object} structured - `caseRow.diff.structured` for a pcStep1 case
 * @returns {{tone: "good"|"warn"|"bad", headline: string, meta: string}}
 */
export function describeStep1Change(structured = {}) {
  const income = structured.qualifyingMonthlyIncome;
  const status = structured.status;

  const material = [];
  if (status?.changed) {
    material.push(
      `status ${status.baseline || "—"} → ${status.candidate || "—"}`,
    );
  }
  if (income?.changed) {
    const percent =
      income.percentDelta == null ? "" : ` (${income.percentDelta}%)`;
    material.push(`income ${formatDelta(income.delta)}${percent}`);
  }

  const meta = income
    ? `Base ${formatMoney(income.baseline)} · Eval ${formatMoney(income.candidate)}`
    : "No qualifying income reported on either side";

  if (material.length > 0) {
    return {
      tone: "bad",
      headline: `Eval changed ${material.join(" and ")}.`,
      meta,
    };
  }

  const hasWordingChange = Boolean(
    structured.reason?.changed || structured.statusReason?.changed,
  );
  if (hasWordingChange) {
    return {
      tone: "warn",
      headline: "Same status and income — only the write-up wording differs.",
      meta,
    };
  }

  return {
    tone: "good",
    headline: "Identical status, qualifying income, and write-up.",
    meta,
  };
}
